import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Country } from 'src/app/services/countries/countries.service';
import { PartyType, RepresentativeType } from 'src/types';
import {
  ClaimControls,
  ClientControls,
  DebtorControls,
  LegalRelationshipControls,
  RepresentativeControls,
} from './edit-case-dialog.service';

const HOUSE_PARTY_TYPE_INTERNAL_ID = '91-a';
const REQUIRED_LABEL_ERROR = 'generateRequiredLabel';

type ClientValue = ReturnType<FormGroup<ClientControls>['getRawValue']>;
type ClaimValue = ReturnType<FormGroup<ClaimControls>['getRawValue']>;
type DebtorValue = ReturnType<FormGroup<DebtorControls>['getRawValue']>;
type LegalRelationshipValue = ReturnType<
  FormGroup<LegalRelationshipControls>['getRawValue']
>;
type RepresentativeValue = ReturnType<
  FormGroup<RepresentativeControls>['getRawValue']
>;

function required(label: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const missing =
      value == null ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0);

    return missing ? { [REQUIRED_LABEL_ERROR]: label } : null;
  };
}

function addressForm(
  address: ClientValue['address'] | DebtorValue['address'] | undefined,
  prefix: string,
): FormGroup {
  return new FormGroup({
    country_id: new FormControl(
      address?.country_id,
      required(prefix ? `${prefix} országa` : 'ország'),
    ),
    postcode: new FormControl(
      address?.postcode,
      required(prefix ? `${prefix} irányítószáma` : 'irányítószám'),
    ),
    settlement: new FormControl(
      address?.settlement,
      required(prefix ? `${prefix} települése` : 'település'),
    ),
    street: new FormControl(
      address?.street,
      required(prefix ? `${prefix} utcája` : 'utca'),
    ),
  });
}

function partyTypeOf(
  partyTypeId: number | null | undefined,
  partyTypes: PartyType[],
): PartyType | undefined {
  return partyTypes.find((partyType) => partyType.id === partyTypeId);
}

function countryIsoOf(
  countryId: number | null | undefined,
  countries: Country[],
): string {
  return countries.find((item) => item.id === countryId)?.iso ?? 'HU';
}

function requiresRegistration(
  individual: boolean,
  countryId: number | null | undefined,
  countries: Country[],
): boolean {
  return !individual && countryIsoOf(countryId, countries) === 'HU';
}

function isRepresentativeTypeOrg(
  representativeType: RepresentativeType | undefined,
): boolean {
  return representativeType?.type?.toLowerCase() === 'org';
}

function representativeRequiresRegistration(
  representativeType: RepresentativeType | undefined,
  representative: RepresentativeValue,
  countries: Country[],
): boolean {
  if (!representativeType) {
    return false;
  }

  const isHungarian =
    countryIsoOf(representative.address.country_id, countries) === 'HU';

  if (!isHungarian) {
    return false;
  }

  if (isRepresentativeTypeOrg(representativeType)) {
    return true;
  }

  return false;
}

export function createLegalRelationshipGenerateForm(
  legalRelationship: LegalRelationshipValue | undefined,
): FormGroup {
  return new FormGroup({
    type_id: new FormControl(
      legalRelationship?.type_id,
      required('jogviszony típusa'),
    ),
  });
}

export function createClaimsGenerateForm(claims: ClaimValue[]): FormArray {
  return new FormArray(
    claims.map((claim, index) => {
      const prefix = index === 0 ? 'követelés' : `${index + 1}. követelés`;
      const file = claim.files[0];

      return new FormGroup({
        type_id: new FormControl(
          claim.type_id,
          required(`${prefix} típusa`),
        ),
        due_date_at: new FormControl(
          claim.due_date_at,
          required(`${prefix} fizetési határideje`),
        ),
        remaining_amount: new FormControl(
          claim.remaining_amount,
          required(`${prefix} összege`),
        ),
        currency_iso: new FormControl(
          claim.currency_iso,
          required(`${prefix} pénzneme`),
        ),
        files: new FormArray([
          new FormGroup({
            invoice_date: new FormControl(
              file?.invoice_date,
              required(`${prefix} számla kelte`),
            ),
            invoice_number: new FormControl(
              file?.invoice_number,
              required(`${prefix} számla sorszáma`),
            ),
          }),
        ]),
      });
    }),
    required('követelés'),
  );
}

function representativesForm(
  representatives: RepresentativeValue[] | undefined,
  representativeTypes: RepresentativeType[],
  countries: Country[],
  path: string,
  requiredLabel = 'képviselők',
): FormArray {
  const controls = (representatives ?? []).map((representative, index) => {
    const representativePath = path
      ? `${path}.${index + 1}`
      : `${index + 1}`;
    const label = `${representativePath}. képviselő`;
    const representativeType = representativeTypes.find(
      (type) => type.id === representative.type_id,
    );
    const country = countries.find(
      (item) => item.id === representative.address.country_id,
    );
    const organisation = isRepresentativeTypeOrg(representativeType);
    const requiresChildren =
      organisation || (!!country && country.iso !== 'HU');
    const requiredChildLabel = organisation
      ? `${label} magánszemély képviselője`
      : `${label} megbízottja`;
    const requireRegistration = representativeRequiresRegistration(
      representativeType,
      representative,
      countries,
    );

    return new FormGroup({
      is_standalone: new FormControl(representative.is_standalone, () =>
        !representative.is_standalone && representatives?.length === 1
          ? {
              [REQUIRED_LABEL_ERROR]: `${label} mellől az együttes képviselő`,
            }
          : null,
      ),
      name: new FormControl(representative.name, required(`${label} neve`)),
      type_id: new FormControl(
        representative.type_id,
        required(`${label} típusa`),
      ),
      registration_number: requireRegistration
        ? new FormControl(
            representative.registration_number,
            required(`${label} nyilvántartási száma`),
          )
        : new FormControl(representative.registration_number),
      registration_office_id: requireRegistration
        ? new FormControl(
            representative.registration_office_id,
            required(`${label} illetékes hivatala`),
          )
        : new FormControl(representative.registration_office_id),
      address: addressForm(representative.address, label),
      representatives:
        requiresChildren
          ? representativesForm(
              representative.representatives,
              representativeTypes,
              countries,
              representativePath,
              requiredChildLabel,
            )
          : new FormArray([]),
    });
  });

  return new FormArray(controls, required(requiredLabel));
}

export function createClientGenerateForm(
  client: ClientValue | undefined,
  partyTypes: PartyType[],
  representativeTypes: RepresentativeType[],
  countries: Country[],
): FormGroup {
  const partyType = partyTypeOf(client?.party_type_id, partyTypes);
  const individual = partyType?.type?.toLowerCase() === 'ind';
  const house = partyType?.internal_id === HOUSE_PARTY_TYPE_INTERNAL_ID;
  const requireRegistration = requiresRegistration(
    !!individual,
    client?.address?.country_id,
    countries,
  );

  return new FormGroup({
    party_type_id: new FormControl(
      client?.party_type_id,
      required('típus'),
    ),
    name: new FormControl(client?.name, required('név')),
    tax_number: house
      ? new FormControl(client?.tax_number)
      : new FormControl(client?.tax_number, required('adószám')),
    address: addressForm(client?.address, ''),
    registration_office_id: requireRegistration
      ? new FormControl(
          partyType?.registration_office_id,
          required('illetékes hivatal'),
        )
      : new FormControl(partyType?.registration_office_id ?? null),
    client_data: new FormGroup({
      registration_number: requireRegistration
        ? new FormControl(
            client?.client_data?.registration_number,
            required('nyilvántartási szám'),
          )
        : new FormControl(client?.client_data?.registration_number),
      mother_name: individual
        ? new FormControl(
            client?.client_data?.mother_name,
            required('anyja neve'),
          )
        : new FormControl(client?.client_data?.mother_name),
      birth_place: individual
        ? new FormControl(
            client?.client_data?.birth_place,
            required('születési hely'),
          )
        : new FormControl(client?.client_data?.birth_place),
      birth_date: individual
        ? new FormControl(
            client?.client_data?.birth_date,
            required('születési idő'),
          )
        : new FormControl(client?.client_data?.birth_date),
    }),
    bank_account: new FormGroup({
      account_number: new FormControl(
        client?.bank_account?.account_number,
        required('bankszámla'),
      ),
    }),
    representatives:
      partyType && !individual
        ? representativesForm(
            client?.representatives,
            representativeTypes,
            countries,
            '',
          )
        : new FormArray([]),
  });
}

function createDebtorGenerateForm(
  debtor: DebtorValue | undefined,
  partyTypes: PartyType[],
  representativeTypes: RepresentativeType[],
  countries: Country[],
): FormGroup {
  const partyType = partyTypeOf(debtor?.party_type_id, partyTypes);
  const individual = partyType?.type?.toLowerCase() === 'ind';
  const house = partyType?.internal_id === HOUSE_PARTY_TYPE_INTERNAL_ID;
  const requireRegistration = requiresRegistration(
    !!individual,
    debtor?.address?.country_id,
    countries,
  );

  return new FormGroup({
    party_type_id: new FormControl(
      debtor?.party_type_id,
      required('típus'),
    ),
    name: new FormControl(debtor?.name, required('név')),
    tax_number:
      individual || house
        ? new FormControl(debtor?.tax_number)
        : new FormControl(debtor?.tax_number, required('adószám')),
    address: addressForm(debtor?.address, ''),
    registration_office_id: requireRegistration
      ? new FormControl(
          partyType?.registration_office_id,
          required('illetékes hivatal'),
        )
      : new FormControl(partyType?.registration_office_id ?? null),
    debtor_data: new FormGroup({
      registration_number: requireRegistration
        ? new FormControl(
            debtor?.debtor_data?.registration_number,
            required('nyilvántartási szám'),
          )
        : new FormControl(debtor?.debtor_data?.registration_number),
    }),
    representatives:
      partyType && !individual
        ? representativesForm(
            debtor?.representatives,
            representativeTypes,
            countries,
            '',
          )
        : new FormArray([]),
  });
}

export function createDebtorsGenerateForm(
  debtors: DebtorValue[],
  partyTypes: PartyType[],
  representativeTypes: RepresentativeType[],
  countries: Country[],
): FormArray {
  return new FormArray(
    debtors.map((debtor) =>
      createDebtorGenerateForm(
        debtor,
        partyTypes,
        representativeTypes,
        countries,
      ),
    ),
    required('adatok'),
  );
}

export function generateMissingLabels(control: AbstractControl): string[] {
  const ownLabel = control.errors?.[REQUIRED_LABEL_ERROR];
  const own = typeof ownLabel === 'string' ? [ownLabel] : [];

  if (control instanceof FormGroup) {
    return [
      ...own,
      ...Object.values(control.controls).flatMap(generateMissingLabels),
    ];
  }

  if (control instanceof FormArray) {
    return [...own, ...control.controls.flatMap(generateMissingLabels)];
  }

  return own;
}
